import React, { useState, useEffect, forwardRef, useImperativeHandle } from 'react'
import {
  Button,
  Slider,
  FormControl,
  FormGroup,
  FormControlLabel,
  Checkbox,
  Divider,
  Box,
  Typography,
  Stack,
  Paper,
} from '@mui/material'
import FormInput from './FormInput'
import localization from '../utils/localization'
import ExtraForm from './ExtraForm'
import ContentUtils from '../utils/content'

const PasswordForm = forwardRef((props, ref) => {
  const [title, setTitle] = useState(props.contentInfo.id ? props.contentInfo.title : '')
  const [titleErr, setTitleErr] = useState('')
  const [account, setAccount] = useState(props.contentInfo.id ? props.contentInfo.account : '')
  const [password, setPassword] = useState(props.contentInfo.id ? props.contentInfo.content : '')
  const [passwordErr, setPasswordErr] = useState('')
  const [passwordLength, setPasswordLength] = useState(12)
  const [littleLetter, setLittleLetter] = useState(true)
  const [bigLetter, setBigLetter] = useState(true)
  const [number, setNumber] = useState(true)
  const [special1, setSpecial1] = useState(false)
  const [special2, setSpecial2] = useState(false)
  const [special3, setSpecial3] = useState(false)
  const [special4, setSpecial4] = useState(false)
  const [extra, setExtra] = useState(props.contentInfo.id ? props.contentInfo.extra : {})

  useEffect(() => {
    if (props.contentInfo.id) {
      setTitle(props.contentInfo.title)
      setTitleErr('')
      setAccount(props.contentInfo.account)
      setPassword(props.contentInfo.content)
      setPasswordErr('')
      setExtra(props.contentInfo.extra)
    }
  }, [props.contentInfo])

  function generatePassword() {
    let passElement = ''
    if (littleLetter) passElement += 'abcdefghijklmnopqrstuvwsyz'
    if (bigLetter) passElement += 'ABCDEFGHIJKLMNOPQRSTUVWSYZ'
    if (number) passElement += '01234567890123456789'
    if (special1) passElement += '!!!!!'
    if (special2) passElement += '#####'
    if (special3) passElement += '@@@@@'
    if (special4) passElement += '$$$$$'

    let passTmp = ''
    if (passElement !== '') {
      for (let i = 0; i < passwordLength; i++) {
        passTmp += passElement[parseInt(Math.random() * passElement.length, 10)]
      }
    }
    setPassword(passTmp)
    setPasswordErr('')
  }

  useImperativeHandle(ref, () => ({
    checkForm() {
      let hasErr = false
      if (title === '') {
        setTitleErr(localization.strings.required)
        hasErr = true
      }
      if (password === '') {
        setPasswordErr(localization.strings.required)
        hasErr = true
      }
      return hasErr
    },
    getContentData() {
      return {
        title: title,
        content: password,
        type: ContentUtils.getPasswordType(),
        color: 'blue',
        account: account,
        extra: extra,
      }
    },
  }))

  return (
    <Stack spacing={3}>
      {/* Basic Info Section */}
      <Stack spacing={2}>
        <FormInput
          label={localization.strings.title}
          value={title}
          placeholder={localization.strings.inputTitleHint}
          error={titleErr}
          onChange={(event) => {
            setTitle(event.target.value)
            setTitleErr('')
          }}
        />
        <FormInput
          label={`${localization.strings.account} (${localization.strings.optional})`}
          value={account}
          placeholder={localization.strings.inputAccountHint}
          onChange={(event) => setAccount(event.target.value)}
        />
      </Stack>

      {/* Password & Generator Section */}
      <Paper
        elevation={0}
        sx={{
          p: 2.5,
          borderRadius: '20px',
          border: '1px solid rgba(0,0,0,0.04)',
          backgroundColor: 'rgba(255,255,255,0.5)',
        }}
      >
        <Stack spacing={2.5}>
          <Stack direction="row" spacing={2} alignItems="flex-end">
            <Box sx={{ flex: 1 }}>
              <FormInput
                wrapContent={true}
                label={localization.strings.password}
                value={password}
                placeholder={localization.strings.inputPasswordHint}
                error={passwordErr}
                onChange={(event) => {
                  setPassword(event.target.value)
                  setPasswordErr('')
                }}
                style={{ width: '100%' }}
              />
            </Box>
            <Button
              variant="contained"
              onClick={generatePassword}
              sx={{
                height: '3.2rem',
                borderRadius: '12px',
                px: 3,
                backgroundColor: '#6366F1',
                textTransform: 'none',
                fontWeight: 700,
                boxShadow: '0 4px 12px rgba(99, 102, 241, 0.2)',
                '&:hover': { backgroundColor: '#4F46E5' },
              }}
            >
              {localization.strings.genPassword}
            </Button>
          </Stack>

          <Box sx={{ px: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800, mb: 1, display: 'block' }}>
              PASSWORD LENGTH: {passwordLength}
            </Typography>
            <Slider
              value={passwordLength}
              step={1}
              min={6}
              max={18}
              valueLabelDisplay="auto"
              onChange={(event) => setPasswordLength(parseInt(event.target.value))}
              sx={{
                color: '#6366F1',
                '& .MuiSlider-thumb': {
                  width: 20,
                  height: 20,
                  backgroundColor: '#fff',
                  border: '2px solid currentColor',
                  '&:hover, &.Mui-focusVisible': { boxShadow: '0px 0px 0px 8px rgba(99, 102, 241, 0.16)' },
                },
              }}
            />
          </Box>

          <Box sx={{ px: 1 }}>
            <Typography variant="caption" sx={{ color: '#64748B', fontWeight: 800, mb: 1, display: 'block' }}>
              OPTIONS
            </Typography>
            <FormGroup row sx={{ gap: 1 }}>
              {[
                { label: 'a-z', state: littleLetter, setter: setLittleLetter },
                { label: 'A-Z', state: bigLetter, setter: setBigLetter },
                { label: '0-9', state: number, setter: setNumber },
                { label: '!', state: special1, setter: setSpecial1 },
                { label: '#', state: special2, setter: setSpecial2 },
                { label: '@', state: special3, setter: setSpecial3 },
                { label: '$', state: special4, setter: setSpecial4 },
              ].map((opt, i) => (
                <FormControlLabel
                  key={i}
                  control={
                    <Checkbox
                      checked={opt.state}
                      size="small"
                      onChange={(e) => opt.setter(e.target.checked)}
                      sx={{
                        color: 'rgba(0,0,0,0.1)',
                        '&.Mui-checked': { color: '#6366F1' },
                      }}
                    />
                  }
                  label={<Typography variant="body2" sx={{ fontWeight: 600 }}>{opt.label}</Typography>}
                  sx={{ m: 0 }}
                />
              ))}
            </FormGroup>
          </Box>
        </Stack>
      </Paper>

      <Divider sx={{ opacity: 0.5 }} />

      {/* Extra Fields Section */}
      <ExtraForm extra={extra} onChange={(e) => setExtra(e)} />
    </Stack>
  )
})

export default PasswordForm
